/**
 * Actions related to authentication and user management
 *
 * Note: Any exported action that can dispatch setAuthError should dispatch
 * setAuthError() at the beginning of the action.
 */
import * as Cookies from 'js-cookie';

import { eventBus, EventKey } from '@built/event-utility';
import l10n from '@built/i18n';
import { ClientMetadata, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Dispatch } from 'react-redux';
import getAuthModule, * as auth from '../auth';
import { PasswordLoginOptions } from '../auth/common';
import * as built from '../built';
import history from '../history';
import {
  getAccessToken,
  getAuthData,
  getBranding,
  getRegistrationData,
  getUserSetupState
} from '../reducers';
import {
  AuthData,
  AuthError,
  AuthErrorType,
  BapiDefaultRole,
  DefaultApp,
  LoginExceptions,
  NotAuthorizedSubstrings,
  RegistrationData,
  SetupState,
  StoreState,
  UserInfo,
  UserOrigin
} from '../types';
import {
  getAuthError,
  getConfig,
  getCookieOptions,
  mapClaimToProduct,
  parseJWTToken
} from '../utils';
import { setEmailVerificationData } from './index';
import { StoreThunk } from './types';

export const SET_AUTH_DATA: 'SET_AUTH_DATA' = 'SET_AUTH_DATA';
export const SET_REGISTRATION_DATA: 'SET_REGISTRATION_DATA' =
  'SET_REGISTRATION_DATA';
export const SET_AUTH_ERROR: 'SET_AUTH_ERROR' = 'SET_AUTH_ERROR';

export interface SetAuthData {
  type: typeof SET_AUTH_DATA;
  authData?: AuthData;
}

export interface SetRegistrationData {
  type: typeof SET_REGISTRATION_DATA;
  registrationData?: RegistrationData;
}

export interface SetAuthError {
  type: typeof SET_AUTH_ERROR;
  error?: AuthError;
}

export interface DealAPICheckUserResponse {
  password: string;
}

// the name of the cookie that console sets upon logout
// to help us redirect the user back to the appropriate
// place
export const BUILT_REDIRECT_COOKIE = 'builtRedirect';

// LOGIN-186
const BUILT_LOGOUT_COOKIE = 'builtLogoutUrl';
const DEAL_AUTH_COOOKIE = 'atlas-authorization';

const authModule = getAuthModule();

/**
 * Set the current authorization data for the logged in user.
 */
export function setAuthData(authData?: AuthData) {
  return {
    type: SET_AUTH_DATA,
    authData
  };
}

/**
 * Set the current registration data for user registration/signup flow.
 */
export function setRegistrationData(registrationData?: RegistrationData) {
  return {
    type: SET_REGISTRATION_DATA,
    registrationData
  };
}

/**
 * Set the current authorization error.
 */
export function setAuthError(error?: AuthError) {
  return {
    type: SET_AUTH_ERROR,
    error
  };
}

/**
 * Login a user. This will involve multiple browser redirections and will
 * eventually result in some local state updates.
 * @param allowExpiredError allow handling expired user migration (Default: true)
 */
export function login(
  options: auth.LoginOptions,
  allowExpiredError: boolean = true
): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    const opts = auth.isPasswordLogin(options)
      ? options
      : { mfaType: getAuthData(getState()).needsMfa!, ...options };

    dispatch(setAuthError());

    return authModule
      .login(opts)
      .catch((err: Error) => {
        if (allowExpiredError) {
          const credentials = <PasswordLoginOptions>options;

          return isExpiredError(err, credentials.username)
            .then((status: boolean) => {
              if (status) {
                // If login attempt results in NotAuthorizedSubstrings.AccountExpired
                // call the API to unexpire the user and make a recursive call, ONCE more
                // to attempt to log in the user.
                dispatch(login(options, false));
                return Promise.resolve(true);
              }

              return Promise.resolve(false);
            })
            .catch((error: Error) => {
              return handleLoginError(err, opts, getState, dispatch);
            });
        } else {
          return handleLoginError(err, opts, getState, dispatch);
        }
      })
      .then((authData) => {
        // LOGIN-184: keep existing auth data state so we don't
        // accidentally lose the ability to migrate IMA users.
        // In the future, we need to be aware of the current
        // state and incoming state changes to make sure we
        // have exactly what's needed after a successful auth.
        dispatch(
          setAuthData({
            ...getState().authData,
            ...(authData as AuthData)
          })
        );
        try {
          eventBus.broadcast(EventKey.LoginUser, {
            userEmail: getState().authData.email
          });
        } catch (err) {
          console.warn('Broadcast failed: ' + err);
        }
        return true;
      })
      .catch((authError: Error) => {
        dispatch(setAuthError(authError));
        return false;
      });
  };
}

/**
 * Attempt to call API unexpire method if error results in NotAuthorizedSubstrings.AccountExpired
 * @param error
 * @param email (optional)
 */
function isExpiredError(error: Error, email?: string): Promise<boolean> {
  if (error.message.indexOf(NotAuthorizedSubstrings.AccountExpired) > -1) {
    return new Promise((resolve, reject) => {
      if (!email) {
        reject(false);
      } else {
        built
          .unexpire(email)
          .then(() => {
            resolve(true);
          })
          .catch(() => {
            // Fall thru to handle 408 or 500 responses
            reject(false);
          });
      }
    });
  }

  return Promise.reject(false);
}

export const checkDealManagementUser = async (
  opts: auth.LoginOptions
): Promise<Response> => {
  // We need to understand if this user is an existing Deal Management User
  // that has yet to be migrated to Cognito
  const loginInfo = opts as PasswordLoginOptions;

  const baseUrl = getConfig('REACT_APP_BUILT_NATIV_API');

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email: loginInfo.username,
      password: loginInfo.password
    })
  };

  return await fetch(`${baseUrl}/nativ-product-api/user/verify`, options);
};

async function handleLoginError(
  err: Error,
  opts: auth.LoginOptions,
  getState: () => StoreState,
  dispatch: Dispatch<StoreState>
): Promise<AuthData> {
  if (
    err.name === LoginExceptions.UserNotFoundException &&
    auth.isPasswordLogin(opts)
  ) {
    return migrateUser(opts).catch(async () => {
      // Check if user is a Deal user that isn't in Cognito yet
      try {
        const response = await checkDealManagementUser(opts);
        if (response && response.ok) {
          const responseBody: DealAPICheckUserResponse = await response.json();
          const loginOpts = {
            username: opts.username,
            password: responseBody.password
          } as PasswordLoginOptions;
          // Reattempt login if user verify is successful
          // should be checking response.status
          const newAuthData = {
            ...getState().authData,
            email: opts.username,
            registering: true,
            newPasswordRequired: true,
            mfaRequired: false,
            isImaUser: false,
            isNativUser: true,
            origin: UserOrigin.DEAL
          };

          dispatch(setAuthData(newAuthData));
          return authModule.login(loginOpts);
        } else {
          return Promise.reject(
            getAuthError(AuthErrorType.INVALID_CREDENTIALS_ERROR)
          );
        }
      } catch {
        return Promise.reject(
          getAuthError(AuthErrorType.INVALID_CREDENTIALS_ERROR)
        );
      }
    });
  } else if (err.name === LoginExceptions.PasswordResetRequiredException) {
    return Promise.reject(
      getAuthError(AuthErrorType.PASSWORD_RESET_REQUIRED_ERROR)
    );
  } else if (err.name === LoginExceptions.NotAuthorizedException) {
    return Promise.reject(handleNotAuthorizedException(err));
  } else if (err.name === LoginExceptions.MockCognitoException) {
    // A generic error thrown by the mockCognito module when
    // the email address provided wasn't found
    return Promise.reject(
      getAuthError(
        AuthErrorType.MOCK_COGNITO_ERROR,
        err.message,
        l10n.t('Auth:Notification.userNotFound', 'User not found')
      )
    );
  } else if (err.name === LoginExceptions.CodeMismatchException) {
    return Promise.reject(getAuthError(AuthErrorType.MFA_VERIFICATION_ERROR));
  } else if (err.name === LoginExceptions.UserNotConfirmedException) {
    return Promise.reject(getAuthError(AuthErrorType.UNVERIFIED_USER_ERROR));
  } else {
    // Unknown error occurred
    return Promise.reject(getAuthError(AuthErrorType.UNKNOWN_ERROR));
  }
}

/**
 * Handles the sub errors of the NotAuthorizedException from Cognito
 * @param err
 */
export function handleNotAuthorizedException(err: Error) {
  if (err.message.indexOf(NotAuthorizedSubstrings.AttemptsExceeded) > -1) {
    // user's account has been temporarily disabled due to too many attempts
    return getAuthError(AuthErrorType.ATTEMPTS_EXCEEDED_ERROR);
  } else if (
    err.message.indexOf(NotAuthorizedSubstrings.AccountDisabled) > -1
  ) {
    // user's account has been disabled by an admin
    return getAuthError(AuthErrorType.ACCOUNT_DISABLED);
  } else if (err.message.indexOf(NotAuthorizedSubstrings.AccountExpired) > -1) {
    // user's account expired before providing initial password
    return getAuthError(AuthErrorType.ACCOUNT_EXPIRED);
  } else if (err.message.indexOf(NotAuthorizedSubstrings.InvalidState) > -1) {
    return getAuthError(AuthErrorType.ACCOUNT_EXPIRED);
  } else if (err.message.indexOf(NotAuthorizedSubstrings.SessionExpired) > -1) {
    return getAuthError(AuthErrorType.SESSION_EXPIRED);
  }

  // anything else is considered invalid credentials
  return getAuthError(AuthErrorType.INVALID_CREDENTIALS_ERROR);
}

/**
 * Handles the LimitExceededException from Cognito
 * @param err
 */
export function handleLimitExceededException(err: Error) {
  return getAuthError(AuthErrorType.LIMIT_EXCEEDED_ERROR);
}

function migrateUser(opts: PasswordLoginOptions) {
  // Check against the built server whether this is a user
  // that can be migrated to Cognito, and log in after they
  // are migrated if possible
  return built
    .migrate(opts.username, opts.password)
    .then(() => authModule.login(opts));
}

/**
 * Logout the current user.
 */
export function logout(redirect = true): StoreThunk {
  return (dispatch) => {
    authModule.logout();
    dispatch(setAuthData());
    dispatch(setAuthError());
    dispatch(setEmailVerificationData());
    if (redirect) {
      history.push('/');
    }
  };
}

/**
 * Save Cognito user data
 */
export function saveUserData(info: UserInfo): StoreThunk {
  return (dispatch) => dispatch(setUserAttributes({ ...info }));
}

/**
 * Tell Built that a user has agreed to ToS
 */
export function agreeToTos(agreedToTos = true): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    const accessToken = getAccessToken(getState());

    if (accessToken) {
      dispatch(setAuthError());
      return built
        .agreeToTos(accessToken)
        .then(() => {
          dispatch(
            setAuthData({
              ...getAuthData(getState()),
              agreedToTos
            })
          );
        })
        .then(() => true)
        .catch((error) => {
          dispatch(setAuthError(error));
          return false;
        });
    }

    dispatch(
      setAuthError({
        message: l10n.t(
          'auth:message.unableToSetToSUserIsNotSignedIn',
          'Unable to set ToS, user is not signed in'
        )
      })
    );
    return Promise.resolve(false);
  };
}

/**
 * Determine whether a given AuthData object indicates that a user is currently
 * authorized.
 */
export function isAuthorized(authData: AuthData) {
  if (authData && authData.idToken && authData.refreshToken) {
    return true;
  }
  return false;
}

/**
 * Route a user based on their authentication state
 */
export function routeUser(): StoreThunk<Promise<boolean>> {
  // TODO (abe) simplify routing and remove tslint ignore below
  // tslint:disable-next-line:cyclomatic-complexity
  return (dispatch, getState) => {
    const state = getState();
    const authData = getAuthData(state) || {};
    let promise = Promise.resolve(true);

    if (authData.maintenanceMode === true) {
      // when BAPI is in maintenance mode, we need to direct them to
      // our maintenance mode view
      history.push({ pathname: '/maintenance', search: location.search });
    } else if (isAuthorized(authData)) {
      promise = dispatch(handleAuthenticatedUser());
    } else if (authData.email && authData.registrationToken) {
      // If we have an email address and valid registration token, then
      // the user needs to finish their initial account setup
      history.push({ pathname: '/setup', search: location.search });
    } else if (location.pathname.indexOf('/reset') !== 0) {
      // Otherwise, the user can only login or reset their password
      history.push({ pathname: '/', search: location.search });
    }

    return promise;
  };
}

/**
 * This function takes the provided AuthData and enforces MFA
 * setup if enabled; if MFA is complete or disabled, the
 * appropriate cookies are set and the user is redirected to
 * console.
 *
 * @param immediate only applicable during welcome back flows
 */
export function handleAuthenticatedUser(): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    const state = getState();
    const authData = state.authData;
    const setupState = getUserSetupState(state);
    let initialize = true;

    if (authData.welcomeBack) {
      //PLATS-2370 -> welcomeBackForm view doesn't work correctly,
      //Instead of routing the user to the welcomeBackForm, we've decided
      //to remove it entirely and redirect the user to their appropriate domain/app
      dispatch(redirectToConsole());
      initialize = false;
    } else if (authData.email && setupState !== SetupState.complete) {
      // The user has an account but they have not completed
      // account setup
      history.replace({ pathname: '/setup', search: location.search });
    } else if (isMfaRequired(authData.idToken)) {
      // if MFA is required and the user hasn't completed their MFA setup
      // we need to kick them over to the setup view
      history.replace({ pathname: '/setup', search: location.search });
    } else {
      // all good, redirect them to console
      dispatch(redirectToConsole());
      initialize = false;
    }

    return Promise.resolve(initialize);
  };
}

/**
 * Redirects the user to the correct location while setting appropriate
 * cookie for where the user should go to logout
 *
 * @param defaultApp optional string representing the application experience the
 * user should redirect to
 *
 * @returns boolean Thunk
 */
export function redirectToConsole(): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    // LOGIN-186 when using a branded login, set the appropriate logout
    // URL in a cookie so console can send the user back to the right spot
    const cookieOptions = getCookieOptions();
    const authData = getAuthData(getState());

    // ENTS-419: pull the `defaultApp` out of the token to help determine
    // where to send the user
    // const { defaultApp } = authData;
    const defaultApp = getDefaultAppFromToken(authData.idToken);

    if (authData && authData.ssoClientId) {
      // LOGIN-194 SSO users need to sign out through Cognito
      // which will redirect them back to the logout endpoint of this
      // app; here we build up the appropriate logout URL to use and
      // set that in the logout cookie that console uses
      const region = getConfig('REACT_APP_REGION');
      const userPoolDomainPrefix = getConfig(
        'REACT_APP_USER_POOL_DOMAIN_PREFIX'
      );
      const logoutUrl = `https://${userPoolDomainPrefix}.auth.${region}.amazoncognito.com/logout?client_id=${authData.ssoClientId}&logout_uri=https://${location.hostname}/logout`;
      Cookies.set(BUILT_LOGOUT_COOKIE, logoutUrl, cookieOptions);
    } else if (getBranding(getState())) {
      // Users should return back to the branded login UX when they
      // logout of console
      const logoutUrl = `${location.origin}/logout`;
      Cookies.set(BUILT_LOGOUT_COOKIE, logoutUrl, cookieOptions);
    } else {
      Cookies.remove(BUILT_LOGOUT_COOKIE, cookieOptions);
    }

    // If a redirectUrl has been set we give it priority,
    // else redirect to console
    if (authData?.redirectUrl) {
      window.location.href = authData?.redirectUrl.href;
    } else {
      if (
        (defaultApp === DefaultApp.NATIV || defaultApp === DefaultApp.DEAL) &&
        authData.idToken
      ) {
        Cookies.set(DEAL_AUTH_COOOKIE, authData.idToken, {
          domain: '.getbuilt.com'
        });
      }
      const selectedRedirectUrl = getRedirectUrl(defaultApp); // assign so we can inspect prior to redirect
      window.location.href = selectedRedirectUrl;
    }

    return Promise.resolve(true);
  };
}

/**
 * Enable SMS MFA when the user already has a verified phone number
 */
export function enableMfa(type: 'sms' | 'totp'): StoreThunk<Promise<boolean>> {
  return (dispatch) => {
    dispatch(setAuthError());

    return authModule
      .enableMfa(type)
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch((error) => {
        dispatch(setAuthError(error));
        return false;
      });
  };
}

/**
 * Disable MFA on the current user's account
 */
export function disableMfa(): StoreThunk<Promise<boolean>> {
  return (dispatch) => {
    dispatch(setAuthError());

    return authModule
      .disableMfa()
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch((error) => {
        dispatch(setAuthError(error));
        return false;
      });
  };
}

/**
 * Set a new user's initial password
 */
export function setInitialPassword(
  newPassword: string,
  clientMetadata?: ClientMetadata
): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    dispatch(setAuthError());

    return authModule
      .setInitialPassword(newPassword, clientMetadata)
      .then((authData) => {
        return dispatch(
          setAuthData({
            ...getState().authData,
            ...authData,
            newPasswordRequired: false
          })
        );
      })
      .then(() => true)
      .catch((error) => {
        dispatch(setAuthError(error));
        return false;
      });
  };
}

/**
 * Sets the user-provided email address
 */
export function setEmailAddress(email: string): StoreThunk<void> {
  return (dispatch, getState) => {
    dispatch(
      setAuthData({
        ...getState().authData,
        userProvidedEmail: email
      })
    );
  };
}

/**
 * Starts the user sign up process; upon successful sign up,
 * the user will be automatically signed in
 */
export function signUpUser(
  username: string,
  password: string,
  registrationToken: string,
  origin?: UserOrigin
): StoreThunk<Promise<boolean>> {
  return async (dispatch, getState) => {
    dispatch(setAuthError());
    const registrationData = getRegistrationData(getState());
    const defaultApp = [
      BapiDefaultRole.BUILDER_ADMINISTRATOR,
      BapiDefaultRole.BUILDER_READONLY
    ].includes(registrationData.defaultRole as BapiDefaultRole)
      ? DefaultApp.B4C
      : undefined;
    let clientMetadata: ClientMetadata;
    if (defaultApp) {
      clientMetadata = {
        default_app_claim: defaultApp
      };
    }

    try {
      await authModule.userSignUp(
        username,
        password,
        registrationToken,
        clientMetadata
      );

      const authData = await authModule.login({ username, password });
      // if user is BAPI user, associate their federated identity
      // if registering in auth service, we do not need to directly associate at this time.
      if (origin !== UserOrigin.LWIO) {
        await built.associateUserAccount(authData.idToken!, registrationToken);
      }
      // user has signed up and logged in; we don't need to collect
      // a password from them from this point forward in the process, so
      // update our auth data state
      const authStateData = getAuthData(getState());
      authStateData.newPasswordRequired = false;
      dispatch(setAuthData(authStateData));
      return true;
    } catch (err: any) {
      dispatch(
        setAuthError({
          ...err,
          title: l10n.t('auth:title.signUpFailure', 'Sign up failure')
        })
      );
      return false;
    }
  };
}

/**
 * Associates an auth provider user with a built api user. The user
 * must already have been authenticated with the auth provider AND
 * this can only be called during the registration/signup process (
 * ideally at the end of the flow).
 */
export function associateUser(): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    dispatch(setAuthError());

    const registrationData = getRegistrationData(getState());
    return authModule
      .getAuthData()
      .then((userAuth) => {
        return new Promise<boolean>((resolve, reject) => {
          // association requires an ID token from Cognito and
          // a Built registration token
          return built
            .associateUserAccount(
              userAuth!.idToken!,
              registrationData.registrationToken
            )
            .then(() => {
              resolve(true);
            });
        });
      })
      .catch((error) => {
        dispatch(
          setAuthError({
            ...error,
            title: l10n.t(
              'auth:title.accountAssociationError',
              'Account association error'
            )
          })
        );
        return false;
      });
  };
}

/**
 * Resend an SMS verification code
 */
export function resendSmsCode(): StoreThunk<Promise<boolean>> {
  return (dispatch) => {
    dispatch(setAuthError());
    return authModule
      .resendSmsCode()
      .then(() => {
        return true;
      })
      .catch((error) => {
        dispatch(setAuthError(error));
        return false;
      });
  };
}

export function setPhoneNumber(
  phoneNumber: string
): StoreThunk<Promise<boolean>> {
  return (dispatch) => {
    dispatch(setAuthError());
    return dispatch(setUserAttributes({ phone_number: phoneNumber }));
  };
}

export function verifyPhoneNumber(
  code: string,
  useForMfa = false
): StoreThunk<Promise<boolean>> {
  return (dispatch) => {
    dispatch(setAuthError());

    if (useForMfa) {
      // If useForMfa is set, verify the phone number and enable MFA in a
      // single step before updating user data
      return authModule
        .verifySmsPhoneNumber(code)
        .then(() => authModule.enableMfa('sms'))
        .then(() => dispatch(updateUserData()))
        .then(() => true)
        .catch((error) => {
          const newError = getAuthError(AuthErrorType.MFA_VERIFICATION_ERROR);
          dispatch(setAuthError(newError));
          return false;
        });
    } else {
      return dispatch(verifyUserAttribute('phone_number', code));
    }
  };
}

export function verifySoftwareToken(
  code: string,
  useForMfa = false
): StoreThunk<Promise<boolean>> {
  return (dispatch) => {
    let promise = authModule.verifySoftwareToken(code);

    dispatch(setAuthError());

    if (useForMfa) {
      // If useForMfa is set, verify the token and enable MFA in a single
      // step before updating user data
      promise = promise.then(() => authModule.enableMfa('totp'));
    }

    return promise
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch((error) => {
        const newError = getAuthError(AuthErrorType.MFA_VERIFICATION_ERROR);
        dispatch(setAuthError(newError));
        return false;
      });
  };
}

/**
 * Store all provided user attributes and retrieve the latest user data
 */
function setUserAttributes(attrs: {
  [name: string]: string;
}): StoreThunk<Promise<boolean>> {
  return (dispatch) => {
    dispatch(setAuthError());

    return authModule
      .setUserAttributes(attrs)
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch((error) => {
        dispatch(setAuthError(error.message));
        return false;
      });
  };
}

/**
 * Provide a code to validate a user attribute. Will generally be used for
 * email or phone number.
 */
function verifyUserAttribute(
  name: string,
  code: string
): StoreThunk<Promise<boolean>> {
  return (dispatch) => {
    dispatch(setAuthError());

    return authModule
      .verifyUserAttribute(name, code)
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch((error) => {
        dispatch(setAuthError(error.message));
        return false;
      });
  };
}

/**
 * Retrieve the latest user data and update the local state with it
 */
function updateUserData(): StoreThunk<Promise<void>> {
  return (dispatch, getState) => {
    return authModule.getUserData().then((userData) => {
      dispatch(
        setAuthData({
          ...getState().authData,
          data: userData
        })
      );
    });
  };
}

/**
 * Refreshes tokens for the active user
 */
export function refreshTokens(): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    return authModule.getUser().then(({ user, session }) => {
      // grab the refresh token from the active user
      const refreshToken = session!.getRefreshToken();

      // refresh the tokens and return a promise
      return new Promise<boolean>((resolve, reject) => {
        user!.refreshSession(
          refreshToken,
          (error, newSession: CognitoUserSession) => {
            if (error) {
              return reject(error);
            }

            dispatch(
              setAuthData({
                ...getState().authData,
                idToken: newSession.getIdToken().getJwtToken(),
                accessToken: newSession.getAccessToken().getJwtToken(),
                refreshToken: newSession.getRefreshToken().getToken()
              })
            );
            return resolve(true);
          }
        );
      });
    });
  };
}

/**
 * When MFA is enabled, we expect the ID token to include
 * a special claim. Here we're checking if that claim is
 * set and if it's true or false
 *
 * @param idToken
 */
function isMfaRequired(idToken: string | undefined): boolean {
  if (!idToken) {
    return false;
  }

  const payload = parseJWTToken(idToken);
  const mfaRequired = payload.mfa_required && payload.mfa_required === 'true';
  if (!mfaRequired) {
    return false;
  }

  const mfaComplete = payload.mfa_complete && payload.mfa_complete === 'true';
  return !mfaComplete;
}

/**
 * Determines the default app from the provided ID token or uses a
 * reasonable default.
 *
 * @param idToken the ID JWT
 * @returns a default app string
 */
function getDefaultAppFromToken(idToken: string | undefined): string {
  let defaultApp = undefined;

  if (idToken !== undefined) {
    const payload = parseJWTToken(idToken);
    defaultApp = payload['apps:default'];
  }

  // if the claim for setting the default app doesn't exist, we fall
  // back to a configurable default app or the 'cla' app
  return defaultApp || getConfig('REACT_APP_DEFAULT_REDIRECT_APP', 'cla');
}

/** Defines the expected shape of our redirect cookie value (stringified as JSON). */
export interface BuiltRedirectCookie {
  /** Target URL to redirect to after sign in. */
  url: string;
  /** Parsed URL object. */
  parsedUrl?: URL;
  /** Environment the cookie was set for. */
  env?: string;
  /** List of apps known to support the given */
  apps?: string[];
}

/**
 * Determines the appropriate redirect URL for the user.
 *
 * If the `BUILT_REDIRECT_COOKIE` is set and contains a valid
 * URL, we'll use that. If not, the provided `defaultApp`
 * will be used to determine the user's default redirect URL
 * and fall back to the CLA application if something is wrong.
 *
 * @param defaultApp optional string representing the application experience the
 * user should redirect to
 *
 * @returns URL to redirect to
 */
function getRedirectUrl(defaultApp: string): string {
  // Map the provided `defaultApp` to a redirect URL or a sane default
  let defaultRedirect = getConfig('REACT_APP_BUILT_CONSOLE_URL');

  if (defaultApp?.startsWith(DefaultApp.B4C)) {
    switch (defaultApp) {
      case DefaultApp.B4C_LWIO:
        defaultRedirect = getConfig('REACT_APP_BUILT_CONSTRUCTION_URL');
        break;
      case DefaultApp.B4C_SELF_SERVE:
        defaultRedirect = getConfig('B4C_SELF_SERVE');
        break;
      case DefaultApp.B4C:
      case DefaultApp.B4C_OWNERS:
        defaultRedirect = getConfig('REACT_APP_BUILT_OWNERS_URL');
        break;
      case DefaultApp.B4C_OWNERS_BORROWER:
        defaultRedirect = getConfig('REACT_APP_BUILT_OWNERS_BORROWER_URL');
        break;
      case DefaultApp.B4C_PROJECT_VENDOR:
        defaultRedirect = getConfig('REACT_APP_BUILT_CONSTRUCTION_URL');
        break;
      case DefaultApp.B4C_VENDOR:
        defaultRedirect = getConfig('REACT_APP_BUILT_VENDORS_URL');
        break;
      default:
        defaultRedirect = getConfig('REACT_APP_BUILT_CONSTRUCTION_URL');
    }
  } else if (
    defaultApp === DefaultApp.DEAL ||
    defaultApp === DefaultApp.NATIV
  ) {
    defaultRedirect = getConfig('REACT_APP_BUILT_NATIV_URL');
  } else if (defaultApp === DefaultApp.B4L) {
    defaultRedirect = getConfig('REACT_APP_BUILT_LENDING_URL');
  }

  const rawRedirectCookie = Cookies.get(BUILT_REDIRECT_COOKIE);

  if (rawRedirectCookie === undefined) {
    return defaultRedirect;
  }

  // remove the cookie after one use
  Cookies.remove(BUILT_REDIRECT_COOKIE, {
    path: getConfig('REACT_APP_BUILT_COOKIE_PATH'),
    domain: getConfig('REACT_APP_BUILT_COOKIE_DOMAIN')
  });

  try {
    const redirectCookie = JSON.parse(
      decodeURIComponent(rawRedirectCookie)
    ) as BuiltRedirectCookie;

    if (isSupportedRedirect(redirectCookie, defaultApp, defaultRedirect)) {
      return redirectCookie.url;
    }

    return defaultRedirect;
  } catch (e) {
    console.warn('Unexpected error checking redirect cookie.', e);
    return defaultRedirect; // fall back to default
  }
}

function get2ndLevelDomain(hostname: string): string {
  // last two parts separated by dots is the 2nd level domain
  return hostname.split('.').slice(-2).join('.');
}

function deriveEnvFromUrl(url: URL): string {
  // our URLs are like https://<app>-<env>.getbuilt.com
  // if not present, it means we're in prod
  return url.hostname.split('.')[0].split('-')[1] ?? 'production';
}

/** Get whether or not to respect the discovered redirect cookie. */
function isSupportedRedirect(
  redirectCookie: BuiltRedirectCookie,
  defaultApp: string,
  defaultRedirect: string
): boolean {
  try {
    redirectCookie.parsedUrl = new URL(redirectCookie.url);
  } catch (ex) {
    console.warn(
      'Unable to parse redirect cookie url: ' + redirectCookie.url,
      ex
    );
    return false;
  }

  // if this fails to parse, we have bigger probs
  const parsedDefaultRedirect = new URL(defaultRedirect);

  if (isInternalSSORedirectUrl(redirectCookie)) {
    return true;
  }

  // these checks (added with LOGIN-178) are to handle the case where a redir cookie may be set (such as on sign out)
  // but the user goes to a different app or environment than the cookie was set for
  // without these checks, that can result in a redirect loop
  // because signing in to "this" env/app may not authenticate them to the target env/app

  // allow the cookie to explicitly tell us which apps it supports
  if (redirectCookie.env && Array.isArray(redirectCookie.apps)) {
    const currentEnv = getConfig(
      'REACT_APP_PROCESS_ENV',
      deriveEnvFromUrl(parsedDefaultRedirect)
    );
    const justAppName = defaultApp.split(':')[0]; // default app may include parts to direct to specific experience

    // in 2.0 & any 1.0 MFEs using the newer auth utilities, "prod" is used for the env;
    // in 1.0, "production"; this is causing issues on the env comparison.
    const envMatches =
      currentEnv === redirectCookie.env ||
      (currentEnv === 'production' && redirectCookie.env === 'prod');

    return (
      envMatches && redirectCookie.apps.includes(mapClaimToProduct(justAppName))
    );
  }

  // try to match based on full hostname (this is backwards compat for cookies that don't give us their supported)
  return redirectCookie.parsedUrl.hostname === parsedDefaultRedirect.hostname;
}

/**
 * Checks whether the redirect cookie is one set by this app, which
 * is used to redirect users back to our api.getbuilt.com/sso endpoint
 * for further processing.
 */
function isInternalSSORedirectUrl(redirectCookie: BuiltRedirectCookie) {
  const idAppDomain = get2ndLevelDomain(window.location.hostname);
  const redirectDomain = get2ndLevelDomain(redirectCookie.parsedUrl!.hostname);

  // Currently redirecting to our sso login page is the only valid usage
  return (
    idAppDomain === redirectDomain &&
    redirectCookie.parsedUrl!.pathname.startsWith('/sso')
  );
}
